import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import { Children, type FC, useContext } from 'react'

import { Trigger as RadixTabsTrigger } from '@radix-ui/react-tabs'

import { TabsContext } from './Tabs'
import { TabsTextCell } from './TabsTextCell'
import type { TabsCellProps } from './types'

/**
 * Individual tab (Trigger)
 */
export const TabsCell: FC<TabsCellProps> = ({ value, disabled, children }) => {
  const { variant, size } = useContext(TabsContext)

  // Check if children contain only text (string/number)
  const hasOnlyTextContent = Children.toArray(children).every(
    child => typeof child === 'string' || typeof child === 'number',
  )

  // If it's simple text, wrap it automatically in TabsTextCell
  const wrappedChildren = hasOnlyTextContent ? <TabsTextCell>{children}</TabsTextCell> : children

  // Extract sub-blocks to ensure Badge then TextCell order
  const blocks = getTypedBlocksByTypes<{ TabsTextCell: object; TabsBadge: object }>(wrappedChildren)

  return (
    <RadixTabsTrigger
      value={value}
      disabled={disabled}
      className={cn(
        'tabs-cell',
        `tabs-cell-variant-${variant}`,
        `tabs-cell-size-${size}`,
        disabled && 'tabs-cell-disabled',
      )}
      tabIndex={disabled ? -1 : 0}
    >
      {blocks.TabsTextCell}
      {blocks.TabsBadge}
    </RadixTabsTrigger>
  )
}

TabsCell.displayName = 'TabsCell'
